// Every Rise Vision template is 1920x1080 (1080x1920 on portrait displays), so screen shares are sent as a constant
// frame of that size.
// Some hardware decoders (e.g. MediaTek on Avocor K-series) fail permanently when the incoming H.264 frame
// size changes mid-stream, which happens whenever the shared tab/window is resized or a video goes fullscreen.
// Compositing the capture onto a fixed black canvas keeps the encoded size constant while still showing resizes.
import {
  createLetterboxTransform,
  FIXED_FRAME_HEIGHT,
  FIXED_FRAME_WIDTH,
} from './fixed-frame-compose';

export {
  computeLetterbox,
  FIXED_FRAME_HEIGHT,
  FIXED_FRAME_WIDTH,
  getFixedFrameSize,
} from './fixed-frame-compose';
export type { FrameOrientation, FrameRect } from './fixed-frame-compose';

// Insertable Streams for MediaStreamTrack (Chromium). Not in lib.dom yet, so declare the parts we use.
interface TrackProcessor {
  readable: ReadableStream<VideoFrame>;
}
interface TrackGenerator extends MediaStreamTrack {
  writable: WritableStream<VideoFrame>;
}
interface InsertableStreamsGlobals {
  MediaStreamTrackProcessor?: new (init: {
    track: MediaStreamTrack;
  }) => TrackProcessor;
  MediaStreamTrackGenerator?: new (init: { kind: 'video' }) => TrackGenerator;
  OffscreenCanvas?: typeof OffscreenCanvas;
  VideoFrame?: typeof VideoFrame;
  ImageCapture?: new (track: MediaStreamTrack) => {
    grabFrame(): Promise<ImageBitmap>;
  };
}

export function isFixedFrameSupported(): boolean {
  const globals = globalThis as unknown as InsertableStreamsGlobals;
  return (
    typeof globals.MediaStreamTrackProcessor === 'function' &&
    typeof globals.MediaStreamTrackGenerator === 'function' &&
    typeof globals.OffscreenCanvas === 'function' &&
    typeof globals.VideoFrame === 'function'
  );
}

// How long to wait for the worker script to load before compositing on the page instead.
const WORKER_READY_TIMEOUT_MS = 2_000;
// How long a picture of a still screen may take before it's given up on; a later one would be out of date.
const STILL_PICTURE_TIMEOUT_MS = 2_000;

/**
 * Starts the compositing worker and hands it the streams once it has loaded. Calls `onFallback` (with the streams
 * still usable on the page) if workers aren't available, the script fails to load, or the transfer fails. Calls
 * `onFailure` if the worker's pipeline fails after it took the streams, when they can no longer be used here.
 */
function startWorker(
  readable: ReadableStream<VideoFrame>,
  writable: WritableStream<VideoFrame>,
  frameWidth: number,
  frameHeight: number,
  onFallback: (reason: unknown) => void,
  onFailure: (reason: unknown) => void,
  onStillChange: (still: boolean, capturedFrames: number) => void,
): Worker | null {
  if (typeof Worker !== 'function') {
    onFallback('Worker not supported');
    return null;
  }

  let worker: Worker;
  try {
    worker = new Worker(new URL('./fixed-frame.worker', import.meta.url), {
      type: 'module',
    });
  } catch (error) {
    onFallback(error);
    return null;
  }

  let transferred = false;
  let fellBack = false;
  const fallBack = (reason: unknown) => {
    if (transferred || fellBack) {
      return;
    }
    fellBack = true;
    clearTimeout(readyTimeout);
    worker.terminate();
    onFallback(reason);
  };
  const readyTimeout = setTimeout(
    () => fallBack('worker did not load in time'),
    WORKER_READY_TIMEOUT_MS,
  );

  worker.addEventListener('error', (event) => {
    const reason = event.message || 'worker error';
    if (transferred) {
      onFailure(reason);
    } else {
      fallBack(reason);
    }
  });
  worker.addEventListener('message', (event: MessageEvent) => {
    const type = event.data?.type;
    if (type === 'ready' && !transferred && !fellBack) {
      try {
        // Transferring moves the streams to the worker; they can't be used on the page afterwards.
        worker.postMessage({ readable, writable, frameWidth, frameHeight }, [
          readable as unknown as Transferable,
          writable as unknown as Transferable,
        ]);
        transferred = true;
        clearTimeout(readyTimeout);
      } catch (error) {
        fallBack(error);
      }
    } else if (type === 'error' || type === 'stopped') {
      onFailure(event.data?.message ?? type);
    } else if (type === 'still') {
      onStillChange(!!event.data?.still, event.data?.capturedFrames ?? 0);
    }
  });

  return worker;
}

/**
 * Takes the still-screen pictures of one capture for all its fixed-frame pipelines (one per display orientation), one
 * at a time, and gives each pipeline its own copy. Asking for a picture makes Chrome send a frame to every clone of the
 * capture, so pipelines that took their own pictures counted each other's frames as changes and never settled: a
 * picture a second, and the still-screen bitrate switching with it.
 */
export class StillPictures {
  private readonly pipelines = new Set<FixedFrameVideo>();
  private taking = false;

  constructor(readonly source: MediaStreamTrack) {}

  add(video: FixedFrameVideo): void {
    this.pipelines.add(video);
  }

  delete(video: FixedFrameVideo): void {
    this.pipelines.delete(video);
  }

  /** Takes a picture for every pipeline, unless one is already being taken. Says whether it asked for one. */
  take(): boolean {
    const { ImageCapture } = globalThis as unknown as InsertableStreamsGlobals;
    if (typeof ImageCapture !== 'function' || this.taking) {
      return false;
    }
    this.taking = true;

    let done = false;
    const finish = () => {
      done = true;
      this.taking = false;
      clearTimeout(timeout);
    };
    const timeout = setTimeout(finish, STILL_PICTURE_TIMEOUT_MS);

    let picture: Promise<ImageBitmap>;
    try {
      picture = new ImageCapture(this.source).grabFrame();
    } catch {
      finish();
      return true;
    }
    picture.then(
      (image) => {
        if (done) {
          image.close();
          return;
        }
        finish();
        void this.share(image);
      },
      () => {
        if (!done) {
          finish();
        }
      },
    );
    return true;
  }

  private async share(image: ImageBitmap): Promise<void> {
    const [first, ...others] = this.pipelines;
    if (!first) {
      image.close();
      return;
    }
    // Handing a picture to a worker moves it there, so the other pipelines get copies.
    const copies = await Promise.allSettled(
      others.map(() => createImageBitmap(image)),
    );
    first.showStillPicture(image);
    others.forEach((video, index) => {
      const copy = copies[index];
      if (copy.status === 'fulfilled') {
        video.showStillPicture(copy.value);
      }
    });
  }
}

/**
 * Draws every captured frame onto a fixed-size black canvas and exposes the result as a video track.
 *
 * The drawing runs in a dedicated worker. The Rise Apps tab is hidden whenever the user shares another tab or their
 * screen, and Chrome deprioritizes a hidden page's main thread, which dropped the stream to a few fps when this ran
 * on the page. The page is only used as a fallback when a worker can't be started.
 *
 * `onFailure` is called once if the pipeline stops producing frames while the video is in use (the worker crashed or
 * its pipeline errored, or the page pipeline failed). The track is dead from then on, so the owner has to replace it.
 *
 * Chrome sometimes never hands the pipeline the frame of a change that leaves the screen still (a slide with animation
 * followed by a still slide), so the display kept showing the previous slide until the screen changed again. So once
 * the screen goes still, a picture of it is taken from the capture and drawn in place of the last frame.
 */
export class FixedFrameVideo {
  readonly track: MediaStreamTrack;
  private readonly generator: TrackGenerator;
  private readonly input: MediaStreamTrack;
  // Kept for the whole share: only its readable is used after it's handed to the worker, and nothing else would keep
  // the processor itself alive.
  private readonly processor: TrackProcessor;
  private worker: Worker | null = null;
  // Set once the pipeline runs on the page; the worker's pipeline is refreshed through a message instead.
  private refreshOnPage: (() => void) | null = null;
  private redrawOnPage:
    | ((image: ImageBitmap, capturedFrames: number) => void)
    | null = null;
  private readonly stillPictures: StillPictures;
  private stopped = false;
  private failed = false;
  private stillScreen = false;
  // The captured frame count the pipeline last reported.
  private capturedFrames = 0;
  // The captured frame count when the last still picture was taken.
  private stillPictureFrames: number | null = null;

  constructor(
    source: MediaStreamTrack,
    private readonly frameWidth = FIXED_FRAME_WIDTH,
    private readonly frameHeight = FIXED_FRAME_HEIGHT,
    private readonly onFailure?: (reason: unknown) => void,
    {
      // false composites on the page from the start, e.g. to retry after the worker pipeline failed.
      useWorker = true,
      // Called when the captured screen goes still (only keepalives are sent) or starts changing again.
      onStillChange,
      // Shared by the pipelines of one capture, so they take its pictures together; this pipeline's own otherwise.
      stillPictures,
    }: {
      useWorker?: boolean;
      onStillChange?: (still: boolean) => void;
      stillPictures?: StillPictures;
    } = {},
  ) {
    const globals = globalThis as unknown as InsertableStreamsGlobals;
    // Each pipeline reads its own clone, so landscape and portrait pipelines can share one capture.
    this.input = source.clone();
    try {
      this.processor = new globals.MediaStreamTrackProcessor!({
        track: this.input,
      });
      this.generator = new globals.MediaStreamTrackGenerator!({
        kind: 'video',
      });
    } catch (error) {
      // The caller falls back to the raw capture; don't leave this clone keeping the capture source alive.
      this.input.stop();
      throw error;
    }
    this.track = this.generator;
    this.stillPictures = stillPictures ?? new StillPictures(this.input);
    this.stillPictures.add(this);
    const processor = this.processor;

    const setStill = (still: boolean, capturedFrames: number) => {
      if (this.stopped) {
        return;
      }
      this.capturedFrames = capturedFrames;
      if (still === this.stillScreen) {
        return;
      }
      this.stillScreen = still;
      onStillChange?.(still);
      if (still) {
        this.takeStillPicture(capturedFrames);
      }
    };

    const composeOnPage = (reason: unknown) => {
      this.worker = null;
      if (this.stopped) {
        return;
      }
      console.warn(
        'Fixed-frame worker unavailable; compositing on the page instead.',
        reason,
      );
      try {
        processor.readable
          .pipeThrough(
            createLetterboxTransform(
              frameWidth,
              frameHeight,
              undefined,
              (fn) => {
                this.refreshOnPage = fn;
              },
              undefined,
              undefined,
              setStill,
              (fn) => {
                this.redrawOnPage = fn;
              },
            ),
          )
          .pipeTo(this.generator.writable)
          .catch((error) => this.fail(error));
      } catch (error) {
        this.fail(error);
      }
    };

    if (!useWorker) {
      composeOnPage('page compositing requested');
      return;
    }

    this.worker = startWorker(
      processor.readable,
      this.generator.writable,
      frameWidth,
      frameHeight,
      composeOnPage,
      (reason) => this.fail(reason),
      setStill,
    );
  }

  private fail(reason: unknown): void {
    if (this.stopped || this.failed) {
      return;
    }
    this.failed = true;
    console.warn('Fixed-frame video pipeline failed', reason);
    // Async, so the owner sees it after the constructor returned even when the failure happens during it.
    queueMicrotask(() => {
      if (!this.stopped) {
        this.onFailure?.(reason);
      }
    });
  }

  /**
   * Has a picture of the still screen taken from the capture, to draw in place of the last frame. Asking for a picture
   * can make Chrome send the pipeline a frame too, which ends the still screen for a moment; a still screen that only
   * that frame arrived before needs no new picture, or every still screen would take one a second.
   */
  private takeStillPicture(capturedFrames: number): void {
    if (
      this.stillPictureFrames !== null &&
      capturedFrames - this.stillPictureFrames <= 1
    ) {
      return;
    }
    if (this.stillPictures.take()) {
      this.stillPictureFrames = capturedFrames;
    }
  }

  /**
   * A picture of the capture, taken for this pipeline or another one of the same capture: drawn in place of the last
   * frame while the screen is still. Either way the frame it can make Chrome send counts as the picture's.
   */
  showStillPicture(image: ImageBitmap): void {
    if (this.stopped) {
      image.close();
      return;
    }
    this.stillPictureFrames = this.capturedFrames;
    if (!this.stillScreen) {
      image.close();
      return;
    }
    const capturedFrames = this.capturedFrames;
    if (this.worker) {
      this.worker.postMessage({ type: 'redraw', image, capturedFrames }, [
        image,
      ]);
    } else if (this.redrawOnPage) {
      this.redrawOnPage(image, capturedFrames);
    } else {
      image.close();
    }
  }

  get size(): { width: number; height: number } {
    return { width: this.frameWidth, height: this.frameHeight };
  }

  get usesWorker(): boolean {
    return !!this.worker;
  }

  /** Whether the captured screen is still, so only keepalives of the last frame are being sent. */
  get still(): boolean {
    return this.stillScreen;
  }

  /** Re-sends the last frame now instead of on the next keepalive, e.g. for a connection that just came up. */
  refresh(): void {
    if (this.stopped) {
      return;
    }
    if (this.worker) {
      this.worker.postMessage({ type: 'refresh' });
    } else {
      this.refreshOnPage?.();
    }
  }

  /**
   * Sizes the capture this pipeline draws. It reads its own clone, which takes the capture's constraints when it's
   * made and none of their changes after, so a quality change has to reach it here too.
   */
  applyConstraints(constraints: MediaTrackConstraints): Promise<void> {
    return this.input.applyConstraints(constraints);
  }

  stop(): void {
    this.stopped = true;
    this.stillPictures.delete(this);
    this.worker?.terminate();
    this.generator.stop();
    this.input.stop();
  }
}
